import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from './config';
import { toast } from 'react-toastify';
import { jwtDecode } from 'jwt-decode';

const storedAccessToken = localStorage.getItem('accessToken');

let decodedEmail = '';
let decodedPhone = '';
let decodedRole = '';
if (storedAccessToken) {
  try {
    const decodedToken = jwtDecode(storedAccessToken);
    decodedEmail = decodedToken.email;
    decodedPhone = decodedToken.phone;
    decodedRole = decodedToken.role;
  } catch (e) {
    // Token invalide ou corrompu — on vide le localStorage
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
  }
}

const initialState = {
  profile: {},
  phoneConfirmed: false,
  email: decodedEmail,
  phone: decodedPhone,
  role: decodedRole,
  accessToken: storedAccessToken || '',
  refreshToken: localStorage.getItem('refreshToken') || '',
  loading: false,
  isSuccess: false,
  errors: [],
  profilePicture: {},
  contactLoading: false,
  contactError: null
};

// ---------- THUNKS ----------

export const signin = createAsyncThunk(
  'auth/signin',
  async (body, { rejectWithValue, dispatch }) => {
    try {
      const params = {
        email: body.email,
        phone: body.phone,
        password: body.password
      };

      const response = await api.post(
        process.env.REACT_APP_BACKEND_URL + '/auth/signin',
        params,
        { headers: { 'Content-Type': 'application/json' } }
      );

      if (response.data.role !== 'CLIENT') {
        return rejectWithValue({ message: 'NOT_CLIENT' });
      }

      localStorage.setItem('accessToken', response.data.accessToken);
      localStorage.setItem('refreshToken', response.data.refreshToken);

      try {
        await dispatch(getProfile()).unwrap();
      } catch (e) {  // getProfile non bloquant — on continue même si ça échoue
}

      return response.data;
    } catch (error) {
      if (!error.response) throw error;
      return rejectWithValue(error.response.data);
    }
  }
);

export const getProfile = createAsyncThunk(
  'user/profile',
  async (__, { rejectWithValue }) => {
    try {
      const response = await api.get(`/user/profile`);
      return response.data;
    } catch (error) {
      if (!error.response) throw error;
      return rejectWithValue(error.response.data);
    }
  }
);

export const UpdateUser = createAsyncThunk(
  'profile/UpdateUser',
  async ({ auth, formData }, { rejectWithValue }) => {
    try {
      const response = await api.put('/user/profile', formData, {
        headers: { Authorization: auth, 'Content-Type': 'application/json' }
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const UpdateUserBasicInfo = createAsyncThunk(
  'profile/UpdateUserBasicInfo',
  async ({ auth, formData }, { rejectWithValue }) => {
    try {
      const response = await api.put('/user/profile/basicInfo', formData, {
        headers: { Authorization: auth, 'Content-Type': 'application/json' }
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const UpdateUserPhone = createAsyncThunk(
  'profile/UpdateUserPhone',
  async ({ auth, formData }, { rejectWithValue }) => {
    try {
      const response = await api.put('/user/profile/Phone', formData, {
        headers: { Authorization: auth, 'Content-Type': 'application/json' }
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const verifyupdatePhoneClient = createAsyncThunk(
  'signUp/verifyupdatePhoneClient',
  async (codeData, { rejectWithValue }) => {
    try {
      const response = await api.post('user/verify-codePhone-client', codeData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const updatePictureUser = createAsyncThunk(
  'profile/updatePicture',
  async (formData, { rejectWithValue }) => {
    try {
      const response = await api.post('/user/profile-picture', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      window.location.reload();
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const sendContactMessage = createAsyncThunk(
  'contact/sendMessage',
  async ({ email, message }, { rejectWithValue }) => {
    try {
      const response = await api.post(
        'http://127.0.0.1:1234/user/visitor-contact',
        { email, message },
        { headers: { 'Content-Type': 'application/json' } }
      );
      toast.success('Message sent successfully!');
      return response.data;
    } catch (error) {
      toast.error('Failed to send message. Please try again later.');
      return rejectWithValue(error.response?.data || { message: 'Unknown error' });
    }
  }
);

export const updatePassword = createAsyncThunk(
  'user/updatePassword',
  async (body, { rejectWithValue, dispatch }) => {
    try {
      const params = {
        old_password: body.old_password,
        password: body.password,
        passwordConfirm: body.passwordConfirm
      };
      const response = await api.put(`/user/change-password`, params);
      return response.data;
    } catch (error) {
      if (error.response?.status === 401) {
        dispatch(logout());
      }
      if (!error.response) throw error;
      return rejectWithValue(error.response.data);
    }
  }
);

// ---------- SLICE ----------

const authSlice = createSlice({
  name: 'client',
  initialState,
  reducers: {

    // ─── Déconnexion ───────────────────────────────────────
    logout: (state) => {
      state.accessToken = null;
      state.refreshToken = null;
      state.profile = {};
      localStorage.clear();
    },

    // ─── Connexion via Google ──────────────────────────────
    setGoogleTokens: (state, action) => {
      const { accessToken, refreshToken } = action.payload;
      state.accessToken = accessToken;
      state.refreshToken = refreshToken;
      state.isSuccess = true;
      state.loading = false;
      const decoded = jwtDecode(accessToken);
      state.email = decoded.email;
      state.phone = decoded.phone;
      state.role = decoded.role;
    },

  },

  extraReducers: (builder) => {
    builder
      // ─── signin ───────────────────────────────────────────
      .addCase(signin.pending, (state) => {
        state.loading = true;
      })
      .addCase(signin.fulfilled, (state, { payload }) => {
        state.isSuccess = true;
        if (!payload?.accessToken || !payload?.refreshToken) {
          state.isSuccess = false;
          state.errors = ['AccessToken and/or refreshToken not found'];
        } else {
          state.accessToken = payload.accessToken;
          state.refreshToken = payload.refreshToken;
          const decodedToken = jwtDecode(payload.accessToken);
          state.email = decodedToken.email;
          state.phone = decodedToken.phone;
          state.role = decodedToken.role;
        }
        state.loading = false;
      })
      .addCase(signin.rejected, (state, { payload }) => {
        if (payload?.statusCode === 400) {
          state.errors = [payload.message];
          toast.error(payload.message);
        }
        state.isSuccess = false;
        state.loading = false;
      })

      // ─── getProfile ───────────────────────────────────────
      .addCase(getProfile.pending, (state) => {
        state.loading = true;
      })
      .addCase(getProfile.fulfilled, (state, { payload }) => {
        state.isSuccess = true;
        state.profile = payload;
        state.loading = false;
      })
      .addCase(getProfile.rejected, (state, { payload }) => {
        if (payload?.statusCode === 400) {
          state.errors = payload?.message;
        }
        state.isSuccess = false;
        state.loading = false;
      })

      // ─── UpdateUser ───────────────────────────────────────
      .addCase(UpdateUser.pending, (state) => {
        state.loading = true;
      })
      .addCase(UpdateUser.fulfilled, (state, { payload }) => {
        state.isSuccess = true;
        state.profile = { ...state.profile, ...payload };
        state.loading = false;
      })
      .addCase(UpdateUser.rejected, (state, { payload }) => {
        if (payload?.statusCode === 400) state.errors = payload?.message;
        state.isSuccess = false;
        state.loading = false;
      })

      // ─── UpdateUserBasicInfo ──────────────────────────────
      .addCase(UpdateUserBasicInfo.pending, (state) => {
        state.loading = true;
      })
      .addCase(UpdateUserBasicInfo.fulfilled, (state, { payload }) => {
        state.isSuccess = true;
        state.profile = { ...state.profile, ...payload };
        state.loading = false;
      })
      .addCase(UpdateUserBasicInfo.rejected, (state, { payload }) => {
        if (payload?.statusCode === 400) state.errors = payload?.message;
        state.isSuccess = false;
        state.loading = false;
      })

      // ─── UpdateUserPhone ──────────────────────────────────
      .addCase(UpdateUserPhone.pending, (state) => {
        state.loading = true;
      })
      .addCase(UpdateUserPhone.fulfilled, (state, { payload }) => {
        state.isSuccess = true;
        state.profile = { ...state.profile, ...payload };
        state.loading = false;
      })
      .addCase(UpdateUserPhone.rejected, (state, { payload }) => {
        if (payload?.statusCode === 400) state.errors = payload?.message;
        state.isSuccess = false;
        state.loading = false;
      })

      // ─── updatePictureUser ────────────────────────────────
      .addCase(updatePictureUser.pending, (state) => {
        state.loading = true;
      })
      .addCase(updatePictureUser.fulfilled, (state, { payload }) => {
        state.isSuccess = true;
        state.profilePicture = payload;
        state.loading = false;
      })
      .addCase(updatePictureUser.rejected, (state, { payload }) => {
        if (payload?.statusCode === 400) state.errors = payload?.message;
        state.isSuccess = false;
        state.loading = false;
      })

      // ─── updatePassword ───────────────────────────────────
      .addCase(updatePassword.pending, (state) => {
        state.loading = true;
      })
      .addCase(updatePassword.fulfilled, (state) => {
        state.isSuccess = true;
        state.loading = false;
      })
      .addCase(updatePassword.rejected, (state, { payload }) => {
        if (payload?.statusCode === 400) state.errors = payload?.message;
        state.isSuccess = false;
        state.loading = false;
      })

      // ─── sendContactMessage ───────────────────────────────
      .addCase(sendContactMessage.pending, (state) => {
        state.contactLoading = true;
        state.contactError = null;
      })
      .addCase(sendContactMessage.fulfilled, (state) => {
        state.contactLoading = false;
        state.contactError = null;
      })
      .addCase(sendContactMessage.rejected, (state, action) => {
        state.contactLoading = false;
        state.contactError = action.payload?.message || 'Failed to send';
      })

      // ─── verifyupdatePhoneClient ──────────────────────────
      .addCase(verifyupdatePhoneClient.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.response = null;
      })
      .addCase(verifyupdatePhoneClient.fulfilled, (state, action) => {
        state.isLoading = false;
        state.response = action.payload;
        state.phoneConfirmed = true;
      })
      .addCase(verifyupdatePhoneClient.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message;
        state.phoneConfirmed = false;
      });
  }
});

export const { logout, setGoogleTokens } = authSlice.actions;
export default authSlice.reducer;